import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Plus, Search, Edit2, Trash2, X, Check, AlertCircle,
  Eye, EyeOff, Server, RefreshCw, Loader2
} from 'lucide-react';
import { SiMicrosoftazure, SiAmazonaws, SiGooglecloud, SiDigitalocean } from 'react-icons/si';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Cloud provider config
const PROVIDERS = {
  azure: {
    name: 'Azure',
    icon: SiMicrosoftazure,
    bg: 'bg-blue-50',
    text: 'text-blue-600',
    border: 'border-blue-200',
    fields: [
      { key: 'tenant_id', label: 'Tenant ID', type: 'text' },
      { key: 'client_id', label: 'Client ID (App Registration)', type: 'text' },
      { key: 'client_secret', label: 'Client Secret', type: 'password' },
      { key: 'subscription_id', label: 'Subscription ID', type: 'text' }
    ]
  },
  aws: {
    name: 'AWS',
    icon: SiAmazonaws,
    bg: 'bg-orange-50',
    text: 'text-orange-600',
    border: 'border-orange-200',
    fields: [
      { key: 'aws_access_key_id', label: 'Access Key ID', type: 'text' },
      { key: 'aws_secret_access_key', label: 'Secret Access Key', type: 'password' },
      { key: 'aws_region', label: 'Default Region', type: 'select', options: [
        'us-east-1', 'us-west-2', 'eu-west-1', 'ap-south-1', 'ap-southeast-1'
      ]}
    ]
  },
  gcp: {
    name: 'GCP',
    icon: SiGooglecloud,
    bg: 'bg-teal-50',
    text: 'text-teal-600',
    border: 'border-teal-200',
    fields: [
      { key: 'project_id', label: 'Project ID', type: 'text' },
      { key: 'service_account_json', label: 'Service Account JSON', type: 'file' }
    ]
  },
  digitalocean: {
    name: 'DigitalOcean',
    icon: SiDigitalocean,
    bg: 'bg-sky-50',
    text: 'text-sky-600',
    border: 'border-sky-200',
    fields: [
      { key: 'api_token', label: 'API Token', type: 'password' }
    ]
  }
};

// Provider Badge Component
function ProviderBadge({ provider }) {
  const config = PROVIDERS[provider] || PROVIDERS.azure;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text} border ${config.border}`}>
      <Icon className="w-3.5 h-3.5" />
      {config.name}
    </span>
  );
}

// Customer Card Component
function CustomerCard({ customer, onEdit, onDelete, onTestConnection }) {
  const [testing, setTesting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  
  const handleTestConnection = async () => {
    setTesting(true);
    setConnectionStatus(null);
    try {
      const response = await axios.post(`${API_URL}/api/customers/${customer.id}/test-connection`);
      setConnectionStatus(response.data);
    } catch (error) {
      setConnectionStatus({ connected: false, error: 'Connection test failed' });
    }
    setTesting(false);
  };
  
  return (
    <div 
      data-testid={`customer-card-${customer.id}`}
      className="bg-white rounded-lg border border-slate-200 p-5 hover:shadow-md hover:border-primary/50 transition-all duration-150"
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-semibold text-slate-900 mb-2">{customer.name}</h3>
          <ProviderBadge provider={customer.cloud_provider} />
        </div>
        <div className="flex gap-1">
          <button
            data-testid={`edit-customer-${customer.id}`}
            onClick={() => onEdit(customer)}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors"
            title="Edit"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            data-testid={`delete-customer-${customer.id}`}
            onClick={() => onDelete(customer)}
            className="p-2 rounded-lg hover:bg-red-50 text-slate-500 hover:text-red-600 transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      <div className="text-sm text-slate-500 mb-4 space-y-1">
        <div>
          {customer.freshdesk_company_id ? (
            <span>Freshdesk ID: {customer.freshdesk_company_id}</span>
          ) : (
            <span className="text-slate-400">No Freshdesk ID</span>
          )}
        </div>
        {customer.site24x7_group_name && (
          <div className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            <span className="text-xs text-emerald-600">Site24x7: {customer.site24x7_group_name}</span>
          </div>
        )}
      </div>
      
      <div className="flex items-center justify-between">
        <button
          data-testid={`test-connection-${customer.id}`}
          onClick={handleTestConnection}
          disabled={testing}
          className="flex items-center gap-2 text-sm text-primary hover:text-primary-600 font-medium disabled:opacity-50"
        >
          {testing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
          Test Connection
        </button>
        
        {connectionStatus && (
          <div className="text-xs font-medium">
            {connectionStatus.results ? (
              <div className="space-y-0.5">
                {connectionStatus.results.map((r, i) => (
                  <span key={i} className={`flex items-center gap-1 ${r.connected ? 'text-green-600' : 'text-red-600'}`}>
                    {r.connected ? <Check className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                    {r.label}: {r.connected ? 'Connected' : (r.error || 'Failed')}
                  </span>
                ))}
              </div>
            ) : (
              <span className={`flex items-center gap-1 ${connectionStatus.connected ? 'text-green-600' : 'text-red-600'}`}>
                {connectionStatus.connected ? (
                  <><Check className="w-3.5 h-3.5" /> Connected</>
                ) : (
                  <><AlertCircle className="w-3.5 h-3.5" /> {connectionStatus.error || 'Failed'}</>
                )}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Customer Form Modal
function CustomerModal({ customer, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: customer?.name || '',
    cloud_provider: customer?.cloud_provider || 'azure',
    freshdesk_company_id: customer?.freshdesk_company_id || '',
    site24x7_group_name: customer?.site24x7_group_name || '',
    credentials: customer?.credentials || {},
    additional_credentials: customer?.additional_credentials || []
  });
  const [showPasswords, setShowPasswords] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [site24x7Groups, setSite24x7Groups] = useState([]);
  
  const providerConfig = PROVIDERS[formData.cloud_provider];
  
  // Fetch Site24x7 groups on mount
  useEffect(() => {
    axios.get(`${API_URL}/api/site24x7/groups`).then(res => {
      setSite24x7Groups(res.data.groups || []);
    }).catch(() => {});
  }, []);
  
  const handleCredentialChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      credentials: { ...prev.credentials, [key]: value }
    }));
  };
  
  const handleFileChange = async (key, file) => {
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      handleCredentialChange(key, json);
    } catch (e) {
      setError('Invalid JSON file');
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    
    try {
      const payload = {
        name: formData.name,
        cloud_provider: formData.cloud_provider,
        freshdesk_company_id: formData.freshdesk_company_id ? parseInt(formData.freshdesk_company_id) : null,
        site24x7_group_name: formData.site24x7_group_name || null,
        credentials: formData.credentials,
        additional_credentials: formData.additional_credentials.length > 0 ? formData.additional_credentials : null
      };
      
      if (customer?.id) {
        await axios.put(`${API_URL}/api/customers/${customer.id}`, payload);
      } else {
        await axios.post(`${API_URL}/api/customers`, payload);
      }
      
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save customer');
    }
    setSaving(false);
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div 
        data-testid="customer-modal"
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden animate-fade-in"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900">
            {customer?.id ? 'Edit Customer' : 'Add Customer'}
          </h2>
          <button
            data-testid="close-modal-btn"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {error && (
            <div className="p-3 rounded-lg bg-red-50 text-red-600 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}
          
          {/* Customer Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Customer Name
            </label>
            <input
              data-testid="customer-name-input"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-primary focus:ring-1 focus:ring-primary"
              placeholder="Enter customer name"
              required
            />
          </div>
          
          {/* Cloud Provider */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Cloud Provider
            </label>
            <select
              data-testid="cloud-provider-select"
              value={formData.cloud_provider}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                cloud_provider: e.target.value,
                credentials: {}
              }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-primary focus:ring-1 focus:ring-primary"
            >
              {Object.entries(PROVIDERS).map(([key, { name }]) => (
                <option key={key} value={key}>{name}</option>
              ))}
            </select>
          </div>
          
          {/* Freshdesk Company ID */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Freshdesk Company ID
              <span className="text-slate-400 font-normal ml-1">(optional)</span>
            </label>
            <input
              data-testid="freshdesk-id-input"
              type="number"
              value={formData.freshdesk_company_id}
              onChange={(e) => setFormData(prev => ({ ...prev, freshdesk_company_id: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-primary focus:ring-1 focus:ring-primary"
              placeholder="e.g. 12345"
            />
            <p className="mt-1 text-xs text-slate-500">
              Find this in Freshdesk &rarr; Admin &rarr; Companies &rarr; click company &rarr; ID in URL
            </p>
          </div>
          
          {/* Site24x7 Monitor Group */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Site24x7 Monitor Group
              <span className="text-slate-400 font-normal ml-1">(for metrics)</span>
            </label>
            <select
              data-testid="site24x7-group-select"
              value={formData.site24x7_group_name}
              onChange={(e) => setFormData(prev => ({ ...prev, site24x7_group_name: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-primary focus:ring-1 focus:ring-primary"
            >
              <option value="">Auto-detect by name</option>
              {site24x7Groups.map(g => (
                <option key={g.id} value={g.name}>{g.name} ({g.monitor_count} monitors)</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-500">
              Maps this customer to a Site24x7 group for CPU/Memory/Disk metrics
            </p>
          </div>
          
          {/* Dynamic Credential Fields */}
          <div className="pt-4 border-t border-slate-200">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Server className="w-4 h-4" />
              {providerConfig.name} Credentials
            </h3>
            
            {providerConfig.fields.map((field) => (
              <div key={field.key} className="mb-3">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {field.label}
                </label>
                
                {field.type === 'select' ? (
                  <select
                    data-testid={`credential-${field.key}`}
                    value={formData.credentials[field.key] || field.options[0]}
                    onChange={(e) => handleCredentialChange(field.key, e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-primary focus:ring-1 focus:ring-primary"
                  >
                    {field.options.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : field.type === 'file' ? (
                  <div>
                    <input
                      data-testid={`credential-${field.key}`}
                      type="file"
                      accept=".json"
                      onChange={(e) => e.target.files[0] && handleFileChange(field.key, e.target.files[0])}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-primary focus:ring-1 focus:ring-primary text-sm"
                    />
                    {formData.credentials[field.key] && (
                      <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
                        <Check className="w-3 h-3" /> JSON file loaded
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      data-testid={`credential-${field.key}`}
                      type={field.type === 'password' && !showPasswords[field.key] ? 'password' : 'text'}
                      value={formData.credentials[field.key] || ''}
                      onChange={(e) => handleCredentialChange(field.key, e.target.value)}
                      className="w-full px-3 py-2 pr-10 border border-slate-200 rounded-lg focus:border-primary focus:ring-1 focus:ring-primary font-mono text-sm"
                      placeholder={`Enter ${field.label.toLowerCase()}`}
                    />
                    {field.type === 'password' && (
                      <button
                        type="button"
                        onClick={() => setShowPasswords(prev => ({ ...prev, [field.key]: !prev[field.key] }))}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
                      >
                        {showPasswords[field.key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Additional Cloud Credentials (for multi-tenant customers) */}
          <div className="pt-4 border-t border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Additional Cloud Credentials
              </h3>
              <button
                type="button"
                data-testid="add-extra-credentials-btn"
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    additional_credentials: [
                      ...prev.additional_credentials,
                      { label: `Tenant ${prev.additional_credentials.length + 2}`, cloud_provider: prev.cloud_provider }
                    ]
                  }));
                }}
                className="text-xs text-primary hover:text-primary-600 font-medium flex items-center gap-1"
              >
                <Plus className="w-3 h-3" />
                Add Tenant/Subscription
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              For customers with multiple tenants or subscriptions. Resources from all sets are merged into one report.
            </p>

            {formData.additional_credentials.map((extraCred, idx) => {
              const extraProvider = PROVIDERS[extraCred.cloud_provider || formData.cloud_provider] || PROVIDERS.azure;
              return (
                <div key={idx} className="mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <input
                      type="text"
                      value={extraCred.label || ''}
                      onChange={(e) => {
                        const updated = [...formData.additional_credentials];
                        updated[idx] = { ...updated[idx], label: e.target.value };
                        setFormData(prev => ({ ...prev, additional_credentials: updated }));
                      }}
                      className="text-sm font-medium text-slate-700 bg-transparent border-none focus:outline-none"
                      placeholder={`Tenant ${idx + 2}`}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          additional_credentials: prev.additional_credentials.filter((_, i) => i !== idx)
                        }));
                      }}
                      className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <select
                    value={extraCred.cloud_provider || formData.cloud_provider}
                    onChange={(e) => {
                      const updated = [...formData.additional_credentials];
                      updated[idx] = { ...updated[idx], cloud_provider: e.target.value };
                      setFormData(prev => ({ ...prev, additional_credentials: updated }));
                    }}
                    className="w-full px-2 py-1.5 mb-2 text-sm border border-slate-200 rounded focus:border-primary"
                  >
                    {Object.entries(PROVIDERS).map(([key, { name }]) => (
                      <option key={key} value={key}>{name}</option>
                    ))}
                  </select>
                  {(PROVIDERS[extraCred.cloud_provider || formData.cloud_provider] || extraProvider).fields.map((field) => (
                    <div key={field.key} className="mb-2">
                      <label className="block text-xs font-medium text-slate-600 mb-1">{field.label}</label>
                      {field.type === 'select' ? (
                        <select
                          value={extraCred[field.key] || field.options[0]}
                          onChange={(e) => {
                            const updated = [...formData.additional_credentials];
                            updated[idx] = { ...updated[idx], [field.key]: e.target.value };
                            setFormData(prev => ({ ...prev, additional_credentials: updated }));
                          }}
                          className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:border-primary"
                        >
                          {field.options.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type={field.type === 'password' ? 'password' : 'text'}
                          value={extraCred[field.key] || ''}
                          onChange={(e) => {
                            const updated = [...formData.additional_credentials];
                            updated[idx] = { ...updated[idx], [field.key]: e.target.value };
                            setFormData(prev => ({ ...prev, additional_credentials: updated }));
                          }}
                          className="w-full px-2 py-1.5 text-sm font-mono border border-slate-200 rounded focus:border-primary"
                          placeholder={`Enter ${field.label.toLowerCase()}`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </form>
        
        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-slate-200 text-slate-700 font-medium hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            data-testid="save-customer-btn"
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-primary text-white font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {customer?.id ? 'Update' : 'Add'} Customer
          </button>
        </div>
      </div>
    </div>
  );
}

// Delete Confirmation Modal
function DeleteModal({ customer, onClose, onConfirm }) {
  const [deleting, setDeleting] = useState(false);
  
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/customers/${customer.id}`);
      onConfirm();
    } catch (error) {
      console.error('Delete failed:', error);
    }
    setDeleting(false);
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div 
        data-testid="delete-modal"
        className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 animate-fade-in"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
            <AlertCircle className="w-5 h-5 text-red-600" />
          </div>
          <h2 className="text-lg font-bold text-slate-900">Delete Customer</h2>
        </div>
        
        <p className="text-slate-600 mb-6">
          Are you sure you want to delete <strong>{customer.name}</strong>? This action cannot be undone.
        </p>
        
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-slate-200 text-slate-700 font-medium hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            data-testid="confirm-delete-btn"
            onClick={handleDelete}
            disabled={deleting}
            className="px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// Main Customers Page
export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [deletingCustomer, setDeletingCustomer] = useState(null);
  
  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/customers`);
      setCustomers(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load customers');
      console.error(err);
    }
    setLoading(false);
  };
  
  useEffect(() => {
    fetchCustomers();
  }, []);
  
  const filteredCustomers = customers.filter(c => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const handleEdit = async (customer) => {
    try {
      const res = await axios.get(`${API_URL}/api/customers/${customer.id}`);
      setEditingCustomer(res.data);
    } catch {
      setEditingCustomer(customer);
    }
    setShowModal(true);
  };
  
  const handleAddNew = () => {
    setEditingCustomer(null);
    setShowModal(true);
  };
  
  const handleSave = () => {
    setShowModal(false);
    setEditingCustomer(null);
    fetchCustomers();
  };
  
  const handleDeleteConfirm = () => {
    setDeletingCustomer(null);
    fetchCustomers();
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            data-testid="search-customers"
            type="text"
            placeholder="Search customers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>
        
        <button
          data-testid="add-customer-btn"
          onClick={handleAddNew}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary text-white font-medium rounded-lg hover:bg-primary-600 transition-colors shadow-sm"
        >
          <Plus className="w-4 h-4" />
          Add Customer
        </button>
      </div>
      
      {error && (
        <div className="p-4 rounded-lg bg-red-50 text-red-600 mb-6 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}
      
      {/* Customer Grid */}
      {filteredCustomers.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          {searchQuery ? 'No customers match your search' : 'No customers yet. Add your first customer!'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredCustomers.map(customer => (
            <CustomerCard
              key={customer.id}
              customer={customer}
              onEdit={handleEdit}
              onDelete={setDeletingCustomer}
            />
          ))}
        </div>
      )}
      
      {/* Modals */}
      {showModal && (
        <CustomerModal
          customer={editingCustomer}
          onClose={() => { setShowModal(false); setEditingCustomer(null); }}
          onSave={handleSave}
        />
      )}
      
      {deletingCustomer && (
        <DeleteModal
          customer={deletingCustomer}
          onClose={() => setDeletingCustomer(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </div>
  );
}
